import React, { useContext, useEffect, useState } from 'react';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { Modal, ModalContent, ModalOverlay, Progress } from '@chakra-ui/react';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { DialogWrappedComponentProps, FeedbackContext } from '../../../providers/FeedbackProvider';
import ClosePrescriptionBrowserTab from './ClosePrescriptionBrowserTab';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { useIsMobile } from '../../../helpers/useIsMobile';
import { EmissionStep, EmissionStepStatus } from './EmissionStep';
import { SponsoredSlot } from './SponsoredSlot';
import { useEmissionLoadingBanner } from './useEmissionLoadingBanner';
import {
  DOCUMENTS_CREATED_PROGRESS,
  DOCUMENTS_SIGNED_PROGRESS,
  EMISSION_LOADING_CARD_SHADOW,
  EMISSION_LOADING_COLORS,
  EMITTED_BAR_VALUE,
  FONT_ONEST,
  MAX_NON_EMITTED_BAR_VALUE,
  PROGRESS_BAR_STEP_INTERVAL_MS,
  PROGRESS_BAR_TOTAL_STEPS,
} from './constants';

export interface PrescriptionEmittionLoadingProps {
  emitted: boolean;
  isLoading: boolean;
  enableDialogCloseWindowTab: boolean;
}

const getStepsStatus = (progress: number, emitted: boolean): EmissionStepStatus[] => {
  const completedSteps = [
    emitted || progress >= DOCUMENTS_CREATED_PROGRESS,
    emitted || progress >= DOCUMENTS_SIGNED_PROGRESS,
    emitted,
  ];
  const currentStepIndex = completedSteps.indexOf(false);

  return completedSteps.map((isCompleted, index) => {
    if (isCompleted) return 'done';
    return index === currentStepIndex ? 'current' : 'pending';
  });
};

export default function PrescriptionEmissionLoading({
  emitted,
  isLoading,
  enableDialogCloseWindowTab,
}: PrescriptionEmittionLoadingProps) {
  const [timerProgress, setTimerProgress] = useState(0);
  const [isOpen, setIsOpen] = useState(true);
  const { openBaseDialog } = useContext(FeedbackContext);
  const validationCode = usePrescriptionStore((store) => store.validationCode());
  const sponsoredBanner = useEmissionLoadingBanner();
  const isMobile = !!useIsMobile('md');

  useEffect(() => {
    if (!emitted && !isLoading) return;

    let step = 0;

    const timer = setInterval(() => {
      setTimerProgress((prevProgress) => {
        if (emitted) {
          if (enableDialogCloseWindowTab) {
            setIsOpen(false);

            openBaseDialog((dialogProps: DialogWrappedComponentProps) => <ClosePrescriptionBrowserTab />, {
              width: 'auto',
              height: 'auto',
              persist: true,
              backgroundColor: 'transparent',
            });
          }
          clearInterval(timer);
          return EMITTED_BAR_VALUE;
        }

        if (step < PROGRESS_BAR_TOTAL_STEPS && prevProgress < MAX_NON_EMITTED_BAR_VALUE) {
          step++;
          return (step / PROGRESS_BAR_TOTAL_STEPS) * MAX_NON_EMITTED_BAR_VALUE;
        } else {
          clearInterval(timer);
          return prevProgress;
        }
      });
    }, PROGRESS_BAR_STEP_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [emitted, enableDialogCloseWindowTab, isLoading, openBaseDialog]);

  if (!emitted && !isLoading) return null;

  const [documentsCreatedStatus, documentsSignedStatus, smsSentStatus] = getStepsStatus(timerProgress, emitted);

  return (
    <Modal isOpen={isOpen} onClose={() => {}} isCentered motionPreset="slideInBottom">
      <ModalOverlay />
      <ModalContent
        w={'auto'}
        maxW={'none'}
        mx={'16px'}
        p={0}
        bg={'transparent'}
        boxShadow={'none'}
        borderRadius={'0px'}
      >
        <Flex
          flexDir={{ base: 'column', md: 'row' }}
          alignItems={{ base: 'flex-start', md: 'center' }}
          gap={{ base: '32px', md: '36px' }}
          w={{ base: 'calc(100vw - 32px)', md: 'auto' }}
          px={{ base: '16px', md: '24px' }}
          py={{ base: '18px', md: '24px' }}
          bg={'#fff'}
          borderRadius={'24px'}
          boxShadow={EMISSION_LOADING_CARD_SHADOW}
        >
          <Flex flexDir={'column'} gap={'24px'} w={{ base: '100%', md: '404px' }} flexShrink={0}>
            <Flex flexDir={'column'} gap={{ base: '14px', md: '24px' }}>
              <Flex flexDir={'column'} alignItems={'flex-start'} gap={{ base: '14px', md: '8px' }}>
                {validationCode && (
                  <Box px={'4px'} borderRadius={'8px'} bg={EMISSION_LOADING_COLORS.tokenBg}>
                    <Text
                      fontFamily={FONT_ONEST}
                      fontSize={'10px'}
                      fontWeight={700}
                      lineHeight={'19.5px'}
                      letterSpacing={'2px'}
                      textTransform={'uppercase'}
                      color={EMISSION_LOADING_COLORS.primary}
                    >
                      Token {validationCode}
                    </Text>
                  </Box>
                )}

                <Text
                  fontFamily={FONT_ONEST}
                  fontSize={{ base: '20px', md: '28px' }}
                  fontWeight={700}
                  lineHeight={{ base: '26px', md: '36px' }}
                  color={EMISSION_LOADING_COLORS.primary}
                  data-testid={`emission-${emitted ? 'success' : 'loading'}`}
                >
                  {emitted ? 'Tudo pronto!' : 'Assinando e enviando os documentos.'}
                </Text>
              </Flex>

              <Flex flexDir={'column'} gap={{ base: '11px', md: '14px' }}>
                <EmissionStep label={'Documentos criados'} status={documentsCreatedStatus} />
                <EmissionStep label={'Assinando com seu certificado digital'} status={documentsSignedStatus} />
                <EmissionStep label='Enviando receita para o paciente' status={smsSentStatus} />
              </Flex>
            </Flex>

            <Progress
              value={timerProgress}
              h={'6px'}
              borderRadius={'999px'}
              bg={EMISSION_LOADING_COLORS.progressTrack}
              sx={{
                '& > div:first-child': {
                  bg: EMISSION_LOADING_COLORS.primary,
                  borderRadius: '999px',
                  transitionProperty: 'width',
                },
              }}
            />
          </Flex>

          {sponsoredBanner && <SponsoredSlot banner={sponsoredBanner} isMobile={isMobile} />}
        </Flex>
      </ModalContent>
    </Modal>
  );
}
